import fetchServiceCreator, { handleErrors, parseAsJson, postHeaders } from '@tls/ui-request';
import { HttpMethods } from '@common-packages/shared-constants';
import { StatusCodes } from 'http-status-codes';

import config from '../config';
import store from '../store';
import { csrfTokenSelector } from '../shared/store/auth';
import { showPermissionModal } from '../shared/permissionModal/store/actions';
import { clientIdSelector } from '../shared/store/context';

import { handleAccessTokenExpirationDate } from './tokenUtils';
import {
  applyOptionalSessionExpiryCap,
  getExpirationDateFromAuthTokens,
  getSecondsUntilIdTokenExpiry,
} from './tokenExpiryFromJwt';

const replacer = (key, value) => (typeof value === 'undefined' ? null : value);

/** Browsers stringify undefined with setItem → localStorage can hold the literal "undefined", which is truthy. */
const isUsableRefreshToken = value =>
  typeof value === 'string' && value.length > 0 && value !== 'undefined' && value !== 'null';

/** Only one refresh POST at a time (parallel API responses would otherwise each call /api/refresh_token). */
let refreshTokenRequestInFlight = null;

const fetchService = fetchServiceCreator(config.BASE_PATH);

const fetchServiceWrapper = (route, options) => {
  const storeState = store.getState();
  const csrfToken = csrfTokenSelector(storeState);
  const clientId = clientIdSelector(storeState);
  const commonHeaders = { clientId };

  return fetchService(route, {
    ...options,
    headers: {
      ...commonHeaders,
      ...options.headers,
      'csrf-token': csrfToken,
      Authorization: `Bearer ${localStorage.getItem('id_token')}`,
    },
    timeout: 600000,
  });
};

export const httpFetch = method => async ({
  route,
  errorMessage,
  body,
  parseResponseErrorMessage = false,
  suppressNotFoundPermissionModal = false,
}) =>
  fetchServiceWrapper(route, {
    method,
    headers: method.toUpperCase() === HttpMethods.GET ? {} : postHeaders,
    ...(body ? { body: JSON.stringify(body, replacer) } : {}),
  })
    .then(handleErrors({ errorMessage, parseResponseErrorMessage }))
    .then(handleAccessTokenExpirationDate)
    .then(response => handleRefreshAuthToken(response, route))
    .catch(error => {
      if (
        !suppressNotFoundPermissionModal &&
        error.isFetchingError &&
        error.status === StatusCodes.NOT_FOUND
      ) {
        store.dispatch(
          showPermissionModal({
            confirmText: null,
            dismissText: 'Close',
            title: 'Access denied',
            text:
              'You currently do not have permission to complete this task, contact your system administrator if you need access',
          }),
        );
      }
      throw error;
    });

export const httpFetchFile = method => async ({
  route,
  errorMessage = '',
  body,
  parseResponseErrorMessage = false,
}) =>
  fetchServiceWrapper(route, {
    method,
    body,
  }).then(handleErrors({ errorMessage, parseResponseErrorMessage }));

const handleRefreshAuthToken = (response, requestRoute = '') => {
  // OAuth code exchange returns new tokens in the JSON body; onSuccess runs *after* this handler.
  // Until then localStorage still has the *previous* id_token (often expired) and refresh token.
  // Entra revokes the old refresh token when the code is redeemed — a refresh POST here races and
  // fails. Also, /auth has no CSRF in Redux (CSRF comes from ClientRoot customer-data) — POST would
  // be 403 EBADCSRFTOKEN and @tls/ui-request redirects to Access Denied.
  // Use requestRoute, not response.url — behind some proxies/CDNs response.url is empty or wrong.
  if (typeof requestRoute === 'string' && requestRoute.includes('/api/access_token')) {
    return response;
  }

  const storedRefresh = localStorage.getItem('refresh_token');
  if (storedRefresh && !isUsableRefreshToken(storedRefresh)) {
    localStorage.removeItem('refresh_token');
    return response;
  }
  const refreshToken = storedRefresh;

  if (refreshToken) {
    const secondsToRefresh = getSecondsUntilIdTokenExpiry();
    const jwtRefreshTime = config.AUTH.JWT_REFRESH_TIME;

    // Use id_token `exp`, not expiration_date (cap can make the latter always "soon" and spam /api/refresh_token).
    if (secondsToRefresh <= jwtRefreshTime) {
      if (refreshTokenRequestInFlight) {
        return response;
      }

      const csrfToken = csrfTokenSelector(store.getState());
      if (!csrfToken) {
        return response;
      }

      const refreshTokenToSend = localStorage.getItem('refresh_token');
      if (!refreshTokenToSend) {
        return response;
      }

      // Same pattern as OpenAM: remove refresh_token before the POST so polling / parallel requests
      // in the pre-expiry window do not each fire /api/refresh_token. Restore only on failure.
      localStorage.removeItem('refresh_token');

      try {
        refreshTokenRequestInFlight = httpPostAndParse({
          route: '/api/refresh_token',
          body: { refreshToken: refreshTokenToSend },
          parseResponseErrorMessage: false,
        })
          .then(res => {
            const { token: tokenPayload } = res || {};
            const {
              id_token,
              refresh_token,
              // eslint-disable-next-line camelcase
              expires_in,
            } = tokenPayload || {};

            if (id_token) {
              localStorage.setItem('id_token', id_token);
            }
            if (refresh_token) {
              localStorage.setItem('refresh_token', refresh_token);
            } else {
              localStorage.setItem('refresh_token', refreshTokenToSend);
            }
            const base = getExpirationDateFromAuthTokens(id_token, expires_in);
            const expiry = applyOptionalSessionExpiryCap(base);
            localStorage.setItem('expiration_date', expiry.toString());
          })
          .catch(() => {
            localStorage.setItem('refresh_token', refreshTokenToSend);
          })
          .finally(() => {
            refreshTokenRequestInFlight = null;
          });
      } catch (error) {
        localStorage.setItem('refresh_token', refreshTokenToSend);
        refreshTokenRequestInFlight = null;
      }
    }
  }

  return response;
};

export const httpGet = httpFetch(HttpMethods.GET);
export const httpPost = httpFetch(HttpMethods.POST);
export const httpPut = httpFetch(HttpMethods.PUT);
export const httpDelete = httpFetch(HttpMethods.DELETE);
export const httpPostFile = httpFetchFile(HttpMethods.POST);
export const httpPutFile = httpFetchFile(HttpMethods.PUT);

export const httpGetAndParse = params => httpGet(params).then(parseAsJson);
export const httpPostAndParse = params => httpPost(params).then(parseAsJson);
export const httpPutAndParse = params => httpPut(params).then(parseAsJson);
export const httpDeleteAndParse = params => httpDelete(params).then(parseAsJson);
export const httpPostFileAndParse = params => httpPostFile(params).then(parseAsJson);
export const httpGetBlob = params => httpGet(params).then(response => response.blob());

export const httpPostAndGetBlob = params => httpPost(params).then(response => response.blob());

export const encodeParam = uriParam => encodeURIComponent(uriParam);
